import * as React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useGetIdentity, useLogout, Loading, useGetList } from 'react-admin'
import { useTranslate } from 'ra-core'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import RssFeedIcon from '@material-ui/icons/RssFeed'
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer'
import PeopleIcon from '@material-ui/icons/People'
import BarChartIcon from '@material-ui/icons/BarChart'

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(1) },
  hero: {
    padding: theme.spacing(4),
    borderRadius: 20,
    marginBottom: theme.spacing(3),
    background:
      'linear-gradient(135deg, #0f766e 0%, #0d9488 45%, #14b8a6 100%)',
    color: '#fff',
    boxShadow: '0 12px 40px rgba(13,148,136,0.35)',
  },
  heroTitle: { fontWeight: 700, marginBottom: theme.spacing(1) },
  heroSubtitle: { opacity: 0.9, maxWidth: 520 },
  statCard: {
    height: '100%',
    borderRadius: 16,
    border: '1px solid #e2e8f0',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 32px rgba(15,23,42,0.1)',
    },
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  statValue: { fontWeight: 700, fontSize: '2rem', lineHeight: 1.1 },
  quickLinks: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: 16,
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
  },
  quickButton: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderRadius: 10,
  },
}))

const StatCard = ({ icon, label, value, color, bg }) => {
  const classes = useStyles()
  return (
    <Card className={classes.statCard} elevation={0}>
      <CardContent>
        <Box
          className={classes.statIcon}
          style={{ backgroundColor: bg, color }}
        >
          {icon}
        </Box>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {label}
        </Typography>
        <Typography className={classes.statValue}>{value ?? '—'}</Typography>
      </CardContent>
    </Card>
  )
}

StatCard.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string.isRequired,
  bg: PropTypes.string.isRequired,
}

const useResourceTotal = resource => {
  const { total, loading } = useGetList(
    resource,
    { page: 1, perPage: 1 },
    { field: 'id', order: 'DESC' },
  )
  return { total, loading }
}

const Dashboard = () => {
  const classes = useStyles()
  const { identity, loading, loaded } = useGetIdentity()
  const { logout } = useLogout()
  const translate = useTranslate()

  const campaigns = useResourceTotal('campaigns')
  const questions = useResourceTotal('questions')
  const users = useResourceTotal('user')
  const delivery = useResourceTotal('delivery')

  if (loading) return <Loading />
  if (!loading && loaded && !identity) logout()
  if (!identity) return null

  const statsLoading =
    campaigns.loading || questions.loading || users.loading || delivery.loading

  return (
    <Box className={classes.root}>
      <Box className={classes.hero}>
        <Typography variant="h4" className={classes.heroTitle}>
          {translate('surveys.messages.welcome')}, {identity.fullName}
        </Typography>
        <Typography variant="body1" className={classes.heroSubtitle}>
          {translate('surveys.dashboard.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<RssFeedIcon />}
            label={translate('surveys.resources.campaigns')}
            value={statsLoading ? '…' : campaigns.total}
            color="#0d9488"
            bg="#ccfbf1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<QuestionAnswerIcon />}
            label={translate('surveys.resources.questions')}
            value={statsLoading ? '…' : questions.total}
            color="#4338ca"
            bg="#e0e7ff"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PeopleIcon />}
            label={translate('surveys.resources.users')}
            value={statsLoading ? '…' : users.total}
            color="#0369a1"
            bg="#e0f2fe"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<BarChartIcon />}
            label={translate('surveys.resources.delivery')}
            value={statsLoading ? '…' : delivery.total}
            color="#b45309"
            bg="#fef3c7"
          />
        </Grid>
      </Grid>

      <Box className={classes.quickLinks}>
        <Typography variant="h6" gutterBottom>
          {translate('surveys.dashboard.quick_actions')}
        </Typography>
        <Box>
          <Button
            component={Link}
            to="/campaigns"
            variant="contained"
            color="primary"
            className={classes.quickButton}
            startIcon={<RssFeedIcon />}
          >
            {translate('surveys.resources.campaigns')}
          </Button>
          <Button
            component={Link}
            to="/questions"
            variant="outlined"
            color="primary"
            className={classes.quickButton}
            startIcon={<QuestionAnswerIcon />}
          >
            {translate('surveys.resources.questions')}
          </Button>
          <Button
            component={Link}
            to="/user"
            variant="outlined"
            color="primary"
            className={classes.quickButton}
            startIcon={<PeopleIcon />}
          >
            {translate('surveys.resources.users')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default Dashboard
